import React from 'react';
import parseHtml, { domToReact } from 'html-react-parser';

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
    ACTING_AS_OPTIONS,
    ENDPOINT_URLS,
} from '../constants';

const multipleSpacesRegex = /\s\s+/g;

const getItemDisplayText = (item) => {
    if (!item.Display) {
        return '';
    }
    const tempSpan = document.createElement('span');
    tempSpan.innerHTML = item.Display;
    return tempSpan.textContent
        .replace(String.fromCharCode(160), ' ') // &nbsp;
        .replace(multipleSpacesRegex, ' ');
};

const getItemText = (item) => {
    if (!item.Text) {
        return '';
    }
    return item.Text;
};

export const getClientSideSearchResults = (searchText, searchResults, selectedOption) => {
    const cachedSearchResults = searchResults[selectedOption];
    if (!cachedSearchResults) {
        return [];
    }
    const lowerCaseSearch = searchText.toLowerCase();
    return Object.keys(cachedSearchResults)
        .filter((protectedId) => (cachedSearchResults?.[protectedId]))
        .map((protectedId) => cachedSearchResults[protectedId])
        .filter((item) => lowerCaseSearch === ''
            || getItemText(item).toLowerCase().includes(lowerCaseSearch)
            || getItemDisplayText(item).toLowerCase().includes(lowerCaseSearch));
};

const parseOptions = (cssBlock, classnames) => ({
    replace: (domNode) => {
        const {
            attribs,
            children,
            name,
            type,
        } = domNode;

        if (type === 'tag' && name === 'div' && attribs?.class === 'cnqr-user-full-name') {
            return (
                <div
                    className={classnames(`${cssBlock}__item-user-name`, 'cnqr-user-full-name')}
                    data-test="cnqr-user-full-name"
                >
                    {domToReact(children, parseOptions)}
                </div>
            );
        }

        if (type === 'tag' && name === 'div' && attribs?.class === 'cnqr-user-additional-details') {
            return (
                <div className={classnames(`${cssBlock}__item-user-details`, 'cnqr-user-additional-details')}>
                    {domToReact(children, parseOptions)}
                </div>
            );
        }

        return domNode;
    },
});

export const listItemRenderer = (itemData, cssBlock, classnames) => parseHtml(
    itemData.Display || '',
    parseOptions(cssBlock, classnames),
);

const getApiGatewayUrls = () => {
    const hostname = window?.location?.hostname;
    const hostToGwMap = {
        'us2.concursolutions.com': {
            apigwUrl: 'us2.api.concursolutions.com',
            apigwBrowserUrl: 'www-us2.api.concursolutions.com',
        },
        'integration.concursolutions.com': {
            apigwUrl: 'integration.api.concursolutions.com',
            apigwBrowserUrl: 'www-integration.api.concursolutions.com',
        },
        'eu2.concursolutions.com': {
            apigwUrl: 'eu2.api.concursolutions.com',
            apigwBrowserUrl: 'www-eu2.api.concursolutions.com',
        },
        'apj1.concursolutions.com': {
            apigwUrl: 'apj1.api.concursolutions.com',
            apigwBrowserUrl: 'www-us2.apj1.concursolutions.com',
        },
        'www.concurcdc.cn': {
            apigwUrl: 'cn.api.concurcdc.cn',
            apigwBrowserUrl: 'www-cn.api.concurcdc.cn',
        },
        'usg.concursolutions.com': {
            apigwUrl: 'usg.api.concursolutions.com',
            apigwBrowserUrl: 'www-usg.api.concursolutions.com',
        },
    };
    return hostToGwMap[hostname] || { apigwUrl: '', apigwBrowserUrl: '' };
};

const setApiGatewayCookiesIfEnabled = async (jwt) => {
    const hasOneUrlCookie = document.cookie.split(';').some((c) => c.trim().startsWith('one_url='));
    if (hasOneUrlCookie) {
        const gwUrls = getApiGatewayUrls();
        if (gwUrls && gwUrls.apigwBrowserUrl && jwt) {
            const setApiGatewayCookie = async (host) => {
                const url = `https://${host}/set-cookie`;
                return axios.get(url, {
                    headers: { Authorization: `Bearer ${jwt}` },
                    withCredentials: true,
                });
            };
            await Promise.all([
                setApiGatewayCookie(gwUrls.apigwBrowserUrl),
            ]);
        }
    }
};

export const startSessionPost = async (selectedOption, selectedUser, isGov, govAllChecked) => {
    const tmpCorrelationId = uuidv4();

    const govParams = isGov ? {
        incTavsAll: selectedOption === ACTING_AS_OPTIONS.GOV_ALL_USERS,
        incTavsArrangedByMe: selectedOption === ACTING_AS_OPTIONS.GOV_ALL_USERS && govAllChecked,
    } : {};

    try {
        const response = await axios.post(ENDPOINT_URLS.ACTING_AS_START_SESSION, {
            incDelegatees: selectedOption === ACTING_AS_OPTIONS.DELEGATE,
            incProxies: selectedOption === ACTING_AS_OPTIONS.PROXY,
            incSelfAssign: selectedOption === ACTING_AS_OPTIONS.SELF_ASSIGN,
            ...govParams,
            userId: selectedUser,
        }, {
            headers: {
                'concur-correlationid': tmpCorrelationId,
                'Referrer-Policy': 'strict-origin-when-cross-origin',
                Referer: window?.location?.href,
            },
            params: {
                href: window?.location?.href,
            },
        });
        await setApiGatewayCookiesIfEnabled(response.data?.Jwt);
        return { response, tmpCorrelationId };
    } catch (sessionError) {
        return { sessionError, tmpCorrelationId };
    }
};

export const endSessionPost = async () => {
    const tmpCorrelationId = uuidv4();

    try {
        const response = await axios.post(ENDPOINT_URLS.ACTING_AS_END_SESSION, {
            noRedirect: true,
        }, {
            headers: {
                'concur-correlationid': tmpCorrelationId,
                'Referrer-Policy': 'strict-origin-when-cross-origin',
                Referer: window?.location?.href,
            },
            params: {
                href: window?.location?.href,
            },
        });
        await setApiGatewayCookiesIfEnabled(response.data?.Jwt);
        return { response, tmpCorrelationId };
    } catch (sessionError) {
        return { sessionError, tmpCorrelationId };
    }
};
